import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../auth/context/AuthContext';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  TrendingUp, 
  DollarSign, 
  Target, 
  Clock, 
  Play,
  History,
  Settings,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface PricingTable {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
}

interface OptimizationJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  createdAt: Date;
  updatedAt: Date;
  result?: any;
  error?: string;
}

interface OptimizationResultsPageProps {
  jobId?: string;
}

export const OptimizationResultsPage: React.FC<OptimizationResultsPageProps> = ({ jobId }) => {
  const { user } = useAuth();
  const [pricingTables, setPricingTables] = useState<PricingTable[]>([]);
  const [selectedPricingTable, setSelectedPricingTable] = useState<number | null>(null);
  const [optimizationJobs, setOptimizationJobs] = useState<OptimizationJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPricingTables();
    fetchOptimizationJobs();
  }, []);

  const fetchPricingTables = async () => {
    try {
      const response = await fetch('/api/pricing-tables', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setPricingTables(data.data);
      } else {
        setError('Failed to fetch pricing tables');
      }
    } catch (error) {
      setError('Failed to fetch pricing tables');
    }
  };

  const fetchOptimizationJobs = async () => {
    try {
      if (!user?.id) {
        console.error('No user found');
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found');
        return;
      }
      
      const response = await fetch(`/api/optimization/history/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setOptimizationJobs(data.optimizationHistory);
      }
    } catch (error) {
      console.error('Failed to fetch optimization jobs:', error);
      setError('Failed to fetch optimization jobs');
    }
  };

  const startOptimization = async () => {
    console.log('🚀 [FRONTEND DEBUG] startOptimization called');
    console.log('🚀 [FRONTEND DEBUG] selectedPricingTable:', selectedPricingTable);
    console.log('🚀 [FRONTEND DEBUG] user:', user);
    console.log('🚀 [FRONTEND DEBUG] authToken:', localStorage.getItem('authToken'));
    
    if (!selectedPricingTable) {
      console.log('❌ [FRONTEND DEBUG] No pricing table selected');
      setError('Please select a pricing table');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        pricingTableId: selectedPricingTable,
        optimizationConfig: {
          population_size: 20,
          generations: 50,
          mutation_rate: 0.1,
          crossover_rate: 0.8,
          elite_size: 5
        }
      };
      
      console.log('🚀 [FRONTEND DEBUG] Making request to /api/optimization/start');
      console.log('🚀 [FRONTEND DEBUG] Request body:', JSON.stringify(requestBody, null, 2));
      console.log('🚀 [FRONTEND DEBUG] Request headers:', {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      });
      
      const response = await fetch('/api/optimization/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(requestBody),
      });

      console.log('🚀 [FRONTEND DEBUG] Response status:', response.status);
      console.log('🚀 [FRONTEND DEBUG] Response headers:', Object.fromEntries(response.headers.entries()));
      
      const data = await response.json();
      console.log('🚀 [FRONTEND DEBUG] Response data:', JSON.stringify(data, null, 2));
      
      if (data.success) {
        console.log('✅ [FRONTEND DEBUG] Optimization successful, redirecting to results');
        // Show success message and redirect to results
        alert('Python optimization completed successfully!');
        window.location.href = `/optimization/results/${data.jobId}`;
      } else {
        console.log('❌ [FRONTEND DEBUG] Optimization failed:', data.error);
        setError(data.error || 'Python optimization failed');
        console.error('Optimization error:', data.details);
      }
    } catch (error) {
      console.log('❌ [FRONTEND DEBUG] Exception caught:', error);
      setError('Failed to start Python optimization');
      console.error('Optimization error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <Clock className="h-4 w-4 text-blue-500 animate-pulse" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Pricing Optimization Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              AI-powered pricing optimization to maximize your revenue
            </p>
          </div>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>

        {/* Quick Start */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Start New Optimization
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Select Pricing Table
                </label>
                <Select
                  value={selectedPricingTable?.toString() || ''}
                  onValueChange={(value) => setSelectedPricingTable(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a pricing table" />
                  </SelectTrigger>
                  <SelectContent>
                    {pricingTables.map((table) => (
                      <SelectItem key={table.id} value={table.id.toString()}>
                        {table.name} (ID: {table.id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end">
                <Button 
                  onClick={startOptimization} 
                  disabled={loading || !selectedPricingTable}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Running Python Optimization...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Start Python Optimization
                    </>
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            <div className="text-sm text-muted-foreground">
              <p>• Optimization will analyze your pricing data and session behavior</p>
              <p>• Results will show optimal prices for maximum revenue</p>
              <p>• Process typically takes 5-15 minutes depending on data size</p>
            </div>
          </CardContent>
        </Card>

        {/* Recent Optimizations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Recent Optimizations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {optimizationJobs.length === 0 ? (
              <div className="text-center py-8">
                <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No optimization jobs yet</p>
                <p className="text-sm text-muted-foreground">
                  Start your first optimization to see results here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {optimizationJobs.map((job) => (
                  <div key={job.jobId} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(job.status)}
                        <span className="font-medium">Job #{job.jobId}</span>
                      </div>
                      <Badge className={getStatusColor(job.status)}>
                        {job.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progress</span>
                        <span>{job.progress}%</span>
                      </div>
                      <Progress value={job.progress} className="w-full" />
                      
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Started: {new Date(job.createdAt).toLocaleDateString()}</span>
                        <span>Updated: {new Date(job.updatedAt).toLocaleDateString()}</span>
                      </div>
                      
                      {job.status === 'completed' && (
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="outline">
                            View Results
                          </Button>
                          <Button size="sm" variant="outline">
                            Download Report
                          </Button>
                        </div>
                      )}
                      
                      {job.status === 'failed' && job.error && (
                        <div className="text-sm text-red-600 mt-2">
                          Error: {job.error}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Optimizations</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{optimizationJobs.length}</div>
              <p className="text-xs text-muted-foreground">
                {optimizationJobs.filter(j => j.status === 'completed').length} completed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {optimizationJobs.length > 0 
                  ? Math.round((optimizationJobs.filter(j => j.status === 'completed').length / optimizationJobs.length) * 100)
                  : 0}%
              </div>
              <p className="text-xs text-muted-foreground">of optimizations successful</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">$0.00</div>
              <p className="text-xs text-muted-foreground">per visitor (avg)</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
